import React from 'react';
import {
  Bike,
  MapPin,
  CheckCircle,
  X,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const PDVAddressModal = ({
  setShowAddressModal,
  loadingZones,
  deliveryZones,
  selectedDeliveryZone,
  setSelectedDeliveryZone,
  address,
  setAddress
}) => {
  return (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4">
          {/* Overlay escuro */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowAddressModal(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-300">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white text-xl font-bold">Endereço de Entrega</h2>
                    <p className="text-orange-100 text-sm">Informe onde entregar o pedido</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddressModal(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-200px)]">
              
              {/* Seleção de Bairro - OBRIGATÓRIO */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                  <Bike className="w-4 h-4 text-orange-500" />
                  Bairro de Entrega *
                </label>
                
                {loadingZones ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                    <span className="ml-2 text-slate-500">Carregando bairros...</span>
                  </div>
                ) : deliveryZones.length === 0 ? (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 text-center">
                    <p className="text-yellow-700 font-medium">⚠️ Nenhum bairro cadastrado</p>
                    <p className="text-yellow-600 text-sm mt-1">Cadastre bairros em &quot;Bairros e Taxas&quot;</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-1">
                    {deliveryZones.map((zone) => (
                      <button
                        key={zone.id}
                        type="button"
                        onClick={() => {
                          setSelectedDeliveryZone(zone);
                          setAddress(prev => ({ ...prev, neighborhood: zone.name }));
                        }}
                        className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center justify-between ${
                          selectedDeliveryZone?.id === zone.id
                            ? 'border-orange-500 bg-orange-50 shadow-md'
                            : 'border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            selectedDeliveryZone?.id === zone.id 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            <MapPin className="w-5 h-5" />
                          </div>
                          <div>
                            <p className={`font-semibold ${
                              selectedDeliveryZone?.id === zone.id ? 'text-orange-700' : 'text-slate-700'
                            }`}>
                              {zone.name}
                            </p>
                            {zone.estimated_time && (
                              <p className="text-xs text-slate-400">⏱️ {zone.estimated_time}</p>
                            )}
                          </div>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg font-bold text-sm ${
                          selectedDeliveryZone?.id === zone.id
                            ? 'bg-orange-500 text-white'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          R$ {zone.fee.toFixed(2)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Divisor */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-slate-400 text-xs font-medium">ENDEREÇO COMPLETO</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>

              {/* Rua */}
              <div className="space-y-1">
                <label className="text-slate-600 text-sm font-medium">Nome da Rua *</label>
                <input
                  type="text"
                  placeholder="Ex: Rua das Flores"
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                />
              </div>

              {/* Número e Complemento */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-slate-600 text-sm font-medium">Número *</label>
                  <input
                    type="text"
                    placeholder="123"
                    value={address.number}
                    onChange={(e) => setAddress({ ...address, number: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 text-sm font-medium">Complemento</label>
                  <input
                    type="text"
                    placeholder="Apto, Bloco..."
                    value={address.complement}
                    onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
              </div>

              {/* Preview do Endereço */}
              {selectedDeliveryZone && address.street && address.number && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-green-700 font-semibold">Endereço completo:</p>
                      <p className="text-green-600 text-sm">
                        {address.street}, {address.number}
                        {address.complement && ` - ${address.complement}`}
                      </p>
                      <p className="text-green-600 text-sm font-medium">
                        {selectedDeliveryZone.name}
                      </p>
                      <p className="text-green-500 text-xs mt-1">
                        Taxa de entrega: R$ {selectedDeliveryZone.fee.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer com botões */}
            <div className="border-t border-slate-100 px-6 py-4 bg-slate-50 flex gap-3">
              <button
                onClick={() => setShowAddressModal(false)}
                className="flex-1 px-4 py-3.5 bg-white hover:bg-slate-100 border-2 border-slate-200 text-slate-700 rounded-xl transition-all font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!selectedDeliveryZone) {
                    toast.error('Selecione um bairro de entrega');
                    return;
                  }
                  if (!address.street.trim()) {
                    toast.error('Informe o nome da rua');
                    return;
                  }
                  if (!address.number.trim()) {
                    toast.error('Informe o número');
                    return;
                  }
                  setShowAddressModal(false);
                  toast.success('Endereço salvo! 📍', {
                    description: `${address.street}, ${address.number} - ${selectedDeliveryZone.name}`
                  });
                }}
                disabled={!selectedDeliveryZone || !address.street || !address.number}
                className="flex-1 px-4 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Confirmar Endereço
              </button>
            </div>
          </div>
        </div>
  );
};

export default PDVAddressModal;
